import type { TableSort } from '../../../shared/components/SortableTable';
import type { TimeRangePreset } from '../../../shared/components/TimeRangeSelect';
import type { ColumnType } from './datasetFormUtils';
import type { DatasetColumn, EventDatasetFilter } from '../../../types/backend';

// A dataset read with no order_by is newest first.
export const DEFAULT_SORT: TableSort = { key: 'event_time', direction: 'desc' };
// The column types a read can neither filter nor sort by.
export const UNFILTERABLE: ColumnType[] = ['list', 'json'];

export const RANGE_PRESETS: TimeRangePreset[] = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/** A filter being written in the filter bar, before it is added to the read. */
export interface FilterDraft {
  column: string;
  match: 'equals' | 'contains';
  value: string;
  min: string;
  max: string;
}

// The backend reads a number filter as a plain decimal only: no exponent, underscores or padding.
const PLAIN_DECIMAL = /^-?[0-9]+(\.[0-9]+)?$/;

/**
 * The event time of a dataset row as ISO 8601 UTC, or null when the row has none that reads as a time. Athena
 * gives a timestamp as text such as `2026-10-29 08:00:00.000`, in UTC.
 */
export function eventTimeIso(text: string | null | undefined): string | null {
  if (!text) return null;
  const iso = text.trim().replace(' UTC', 'Z').replace(' ', 'T');
  const time = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(time.getTime()) ? null : time.toISOString();
}

// A time picked in the browser's zone is sent as the moment it names, in UTC with a zone.
const zoned = (local: string): string | undefined => (local ? new Date(local).toISOString() : undefined);

/** The filter a draft names for a column of this type, or null while the draft is not a valid filter. */
export function filterOf(draft: FilterDraft, type: DatasetColumn['type']): EventDatasetFilter | null {
  if (type === 'number' || type === 'timestamp') {
    const min = type === 'timestamp' ? zoned(draft.min) : draft.min.trim() || undefined;
    const max = type === 'timestamp' ? zoned(draft.max) : draft.max.trim() || undefined;
    if (min === undefined && max === undefined) return null;
    if (type === 'number' && [min, max].some((bound) => bound !== undefined && !PLAIN_DECIMAL.test(bound))) {
      return null;
    }
    return { column: draft.column, op: 'range', ...(min !== undefined && { min }), ...(max !== undefined && { max }) };
  }
  if (!draft.value) return null;
  const op = type === 'text' && draft.match === 'contains' ? 'contains' : 'in';
  return { column: draft.column, op, values: [draft.value] };
}

/** A filter as its chip names it, such as `severity = HIGH` or `score from 5 before 9`. */
export function labelOf(filter: EventDatasetFilter): string {
  if (filter.op === 'range') {
    const parts = [filter.min !== undefined && `from ${filter.min}`, filter.max !== undefined && `before ${filter.max}`];
    return `${filter.column} ${parts.filter(Boolean).join(' ')}`;
  }
  return `${filter.column} ${filter.op === 'contains' ? 'contains' : '='} ${filter.values?.join(', ')}`;
}
